<template>
  <div class="login-container">
    <div class="login-wrapper">
      <!-- 顶部Logo区域 -->
      <div class="login-header">
        <div class="logo-section">
          <div class="logo-icon">
            <img src="/huahu-icon.jpeg" alt="花狐" class="logo-image" />
          </div>
          <h1 class="app-title">花狐</h1>
        </div>
      </div>

      <!-- 登录表单卡片 -->
      <div class="login-card">
        <el-form 
          ref="formRef" 
          :model="form" 
          :rules="rules" 
          class="login-form"
          size="large"
          @submit.prevent="handleSubmit"
        >
          <el-form-item prop="username" class="form-item">
            <label class="field-label">用户名</label>
            <el-input
              v-model="form.username"
              placeholder="请输入用户名"
              clearable
              class="apple-input"
              autocomplete="username"
              @keyup.enter="handleSubmit"
            />
          </el-form-item>
          
          <el-form-item prop="password" class="form-item">
            <label class="field-label">密码</label>
            <el-input
              v-model="form.password"
              type="password"
              placeholder="请输入密码"
              show-password
              clearable
              class="apple-input"
              autocomplete="new-password"
              @keyup.enter="handleSubmit"
            />
          </el-form-item>
          
          <div class="remember-section">
            <el-checkbox v-model="rememberUsername" class="remember-checkbox">
              记住账号
            </el-checkbox>
            <el-checkbox v-model="rememberPassword" class="remember-checkbox" style="margin-left: 20px;">
              记住密码
            </el-checkbox>
          </div>
          
          <div class="submit-section">
            <el-button 
              type="primary" 
              class="submit-btn"
              :loading="loading"
              @click.prevent="handleSubmit"
            >
              登录
            </el-button>
          </div>
        </el-form>
        
        <!-- 系统说明提示 -->
        <div class="system-notice">
          <div class="notice-content">
            <div class="notice-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
            </div>
            <div class="notice-text">
              <p class="notice-desc">
                需要联系作者开通账号
              </p>
            </div>
          </div>
        </div>
        
        <!-- 网络诊断信息 -->
        <NetworkDiagnostics v-if="showNetworkDiagnostics" />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { login } from '../api/auth'
import NetworkDiagnostics from '../components/NetworkDiagnostics.vue'

export default {
  name: 'Login',
  components: {
    NetworkDiagnostics
  },
  setup() {
    const router = useRouter()
    const formRef = ref()
    const loading = ref(false)
    const showNetworkDiagnostics = ref(false)
    const rememberUsername = ref(false)
    const rememberPassword = ref(false)
    
    const form = reactive({
      username: '',
      password: ''
    })
    
    // Cookie操作函数
    const setCookie = (name, value, days = 30) => {
      const expires = new Date()
      expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000))
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`
    }
    
    const getCookie = (name) => {
      const nameEQ = name + "="
      const ca = document.cookie.split(';')
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i]
        while (c.charAt(0) === ' ') c = c.substring(1, c.length)
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
      }
      return null
    }
    
    const deleteCookie = (name) => {
      document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
    }
    
    const rules = reactive({
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { min: 3, max: 50, message: '用户名长度在3到50个字符', trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
      ]
    })
    
    const handleSubmit = async (event) => {
      // 阻止表单默认提交行为
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }
      
      try {
        // 表单验证
        const valid = await formRef.value.validate()
        if (!valid) {
          return false
        }
        
        loading.value = true
        
        // 登录逻辑
        const response = await login(form.username, form.password)
        localStorage.setItem('token', response.data.token)
        localStorage.setItem('user', JSON.stringify(response.data.user))
        
        // 保存或删除用户名Cookie
        if (rememberUsername.value) {
          setCookie('remembered_username', form.username)
        } else {
          deleteCookie('remembered_username')
        }
        
        // 保存或删除密码Cookie
        if (rememberPassword.value) {
          setCookie('remembered_password', form.password)
        } else {
          deleteCookie('remembered_password')
        }
        
        // 检查用户到期时间，如果是普通用户且还有5天及以内到期，显示提示弹窗
        const user = response.data.user
        if (user && (user.is_admin === 0 || user.is_admin === null) && user.expiry_date) {
          try {
            const expiryDate = new Date(user.expiry_date)
            const now = new Date()
            const diffTime = expiryDate - now
            
            // 如果已过期，不显示提示（后端会阻止登录）
            if (diffTime <= 0) {
              return
            }
            
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
            
            // 如果还有5天及以内到期（5、4、3、2、1天），显示友好提示
            if (diffDays <= 5 && diffDays > 0) {
              const daysText = diffDays === 1 ? '1天' : `${diffDays}天`
              ElMessageBox.alert(
                `您的账户还有${daysText}即将到期，为了不影响正常使用，请及时联系代理续费。`,
                '账户到期提醒',
                {
                  confirmButtonText: '我知道了',
                  type: 'warning',
                  customClass: 'expiry-warning-dialog'
                }
              )
            }
          } catch (error) {
            console.error('检查到期时间失败:', error)
          }
        }
        
        ElMessage.success('登录成功！正在跳转...')
        
        // 延迟跳转，让用户看到成功消息
        setTimeout(() => {
          router.push('/dashboard')
        }, 1000)
      } catch (error) {
        console.error('登录失败:', error)
        
        // 更详细的错误处理
        let errorMessage = '登录失败，请重试'
        
        if (error.response) {
          // 服务器返回的错误
          const status = error.response.status
          const data = error.response.data
          
          // 优先使用后端返回的具体错误信息
          if (data?.message) {
            errorMessage = data.message
          } else if (status === 401) {
            errorMessage = '用户名或密码错误'
          } else if (status === 400) {
            errorMessage = '请求格式错误'
          } else if (status === 500) {
            errorMessage = '服务器内部错误，请稍后重试'
          }
        } else if (error.code === 'ECONNABORTED') {
          errorMessage = '请求超时，请检查网络连接'
        } else if (error.code === 'ERR_NETWORK') {
          errorMessage = '网络错误，请检查后端服务是否启动'
          // 如果是网络错误，显示诊断信息
          showNetworkDiagnostics.value = true
        } else if (error.message) {
          errorMessage = error.message
        }
        
        ElMessage.error(errorMessage)
      } finally {
        loading.value = false
      }
    }
    
    // 页面加载时恢复保存的用户名和密码
    onMounted(() => {
      const savedUsername = getCookie('remembered_username')
      if (savedUsername) {
        form.username = savedUsername
        rememberUsername.value = true
      }
      
      const savedPassword = getCookie('remembered_password')
      if (savedPassword) {
        form.password = savedPassword
        rememberPassword.value = true
      }
    })
    
    return {
      formRef,
      loading,
      form,
      rules,
      handleSubmit,
      showNetworkDiagnostics,
      rememberUsername,
      rememberPassword
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  background-color: #f5f5f7;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.login-wrapper {
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* 顶部Logo区域 */
.login-header {
  text-align: center;
}

.logo-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.logo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-image {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.app-title {
  font-size: 32px;
  font-weight: 700;
  color: #1d1d1f;
  letter-spacing: -0.05em;
  margin: 0;
}

/* 登录卡片 */
.login-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

/* 表单样式 */
.login-form {
  width: 100%;
}

/* 系统说明提示 */
.system-notice {
  margin-top: 24px;
  padding: 16px;
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.1);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.system-notice:hover {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.2);
}

.notice-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.notice-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: #3b82f6;
  margin-top: 2px;
}

.notice-text {
  flex: 1;
}

.notice-title {
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 4px 0;
  line-height: 1.4;
}

.notice-desc {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.back-home-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
  margin-left: 8px;
  transition: all 0.2s ease;
  border-bottom: 1px solid transparent;
}

.back-home-link:hover {
  color: #2563eb;
  border-bottom-color: #2563eb;
}

.form-item {
  margin-bottom: 24px;
}

.field-label {
  display: block;
  font-size: 15px;
  font-weight: 500;
  color: #1d1d1f;
  margin-bottom: 8px;
  letter-spacing: -0.024em;
}

.apple-input :deep(.el-input__wrapper) {
  height: 52px;
  border-radius: 12px;
  border: 1px solid #d2d2d7;
  background-color: #ffffff;
  font-size: 17px;
  padding: 0 16px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: none;
}

.apple-input :deep(.el-input__wrapper:hover) {
  border-color: #007aff;
  box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
}

.apple-input :deep(.el-input__wrapper.is-focus) {
  border-color: #007aff;
  box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.15);
}

.apple-input :deep(.el-input__inner) {
  color: #1d1d1f;
  font-weight: 400;
  letter-spacing: -0.022em;
  height: 100%;
}

.apple-input :deep(.el-input__inner::placeholder) {
  color: #86868b;
  font-weight: 400;
}

/* 记住账号选项 */
.remember-section {
  margin: 16px 0 8px 0;
  display: flex;
  align-items: center;
}

.remember-checkbox :deep(.el-checkbox__label) {
  font-size: 14px;
  color: #86868b;
  font-weight: 400;
  letter-spacing: -0.022em;
}

.remember-checkbox :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background-color: #007aff;
  border-color: #007aff;
}

.remember-checkbox :deep(.el-checkbox__inner) {
  border-radius: 4px;
  border-color: #d2d2d7;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.remember-checkbox :deep(.el-checkbox__inner:hover) {
  border-color: #007aff;
}

/* 提交按钮 */
.submit-section {
  margin: 24px 0 24px 0;
}

.submit-btn {
  width: 100%;
  height: 52px;
  background-color: #007aff;
  border: none;
  border-radius: 12px;
  font-size: 17px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: -0.022em;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.submit-btn:hover {
  background-color: #0051d5;
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(0, 122, 255, 0.3);
}

.submit-btn:active {
  transform: translateY(0);
}

/* 响应式设计 */
@media (max-width: 480px) {
  .login-container {
    padding: 16px;
  }
  
  .login-card {
    padding: 24px;
    border-radius: 16px;
  }
  
  .system-notice {
    margin-top: 20px;
    padding: 14px;
  }
  
  .notice-title {
    font-size: 13px;
  }
  
  .notice-desc {
    font-size: 12px;
  }
  
  .app-title {
    font-size: 28px;
  }
  
  .logo-image {
    width: 70px;
    height: 70px;
    border-radius: 16px;
  }
}

/* 隐藏Element Plus的默认样式 */
:deep(.el-form-item__label) {
  display: none;
}

:deep(.el-form-item__error) {
  font-size: 13px;
  color: #ff3b30;
  font-weight: 400;
  letter-spacing: -0.024em;
  margin-top: 4px;
}

/* 消息提示样式优化 */
:deep(.el-message) {
  border-radius: 12px !important;
  font-weight: 500 !important;
  letter-spacing: -0.022em !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
}

:deep(.el-message--success) {
  background-color: #34c759 !important;
  border-color: #34c759 !important;
}

:deep(.el-message--error) {
  background-color: #ff3b30 !important;
  border-color: #ff3b30 !important;
}

/* 到期提醒弹窗样式 */
:deep(.expiry-warning-dialog) {
  border-radius: 16px;
  padding: 0;
}

:deep(.expiry-warning-dialog .el-message-box__header) {
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

:deep(.expiry-warning-dialog .el-message-box__title) {
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;
  display: flex;
  align-items: center;
  gap: 8px;
}

:deep(.expiry-warning-dialog .el-message-box__title::before) {
  content: '⚠️';
  font-size: 20px;
}

:deep(.expiry-warning-dialog .el-message-box__content) {
  padding: 20px 24px;
  font-size: 15px;
  line-height: 1.6;
  color: #1d1d1f;
}

:deep(.expiry-warning-dialog .el-message-box__btns) {
  padding: 16px 24px 24px 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

:deep(.expiry-warning-dialog .el-button--primary) {
  background-color: #007aff;
  border-color: #007aff;
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s ease;
}

:deep(.expiry-warning-dialog .el-button--primary:hover) {
  background-color: #0051d5;
  border-color: #0051d5;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
}
</style> 